import React from 'react';
import {
  Layout,
  Stack,
  SegmentedSelection,
  Card,
  TextFieldControl,
  TagsInputControl,
  FilteredSelectControl,
  Text,
  SwitcherControl,
  FormLabel,
  formatDate,
} from '@piwikpro/ui-components';
import { TFunction } from 'react-i18next';
import { SPLIT_SEPARATORS } from '@piwikpro/platform';
import type { Timezone as TimezoneCurrencyItem } from '@piwikpro/platform/config/timeZones';

interface BasicConfigurationStepProps {
  t: TFunction
  selectedOption: 'website' | 'mobile-app'
  onChangeSelectedOption: (value: 'website' | 'mobile-app') => void
  timezones: TimezoneCurrencyItem
  currencies: TimezoneCurrencyItem
  timezoneSearch: string
  onTimezoneSearch: (value: string) => void
  onTimezoneOpen: () => void
  currencySearch: string
  onCurrencySearch: (value: string) => void
  onCurrencyOpen: () => void
  previewTimezone: string
}

const BasicConfigurationStep: React.FC<BasicConfigurationStepProps> = ({
  t,
  selectedOption,
  onChangeSelectedOption,
  timezones,
  currencies,
  timezoneSearch,
  onTimezoneSearch,
  onTimezoneOpen,
  currencySearch,
  onCurrencySearch,
  onCurrencyOpen,
  previewTimezone,
}) => {
  const searchValueMatchesItem = (searchValue: string, item: TimezoneCurrencyItem[0]) =>
    item.name?.toLowerCase().includes(searchValue.toLowerCase());

  const filteredTimezones = timezones.filter(item => searchValueMatchesItem(timezoneSearch, item));
  const filteredCurrencies = currencies.filter(item => searchValueMatchesItem(currencySearch, item));
  const timezoneOption = timezones.find(item => item.id === (previewTimezone || 'UTC'));

  return (
    <Layout maxWidth="medium">
      <Stack vertical spacing="wide">
        <Stack.Item />
        <Stack.Item fill>
          <SegmentedSelection
            fullWidth
            items={[
              { value: 'website', name: t('apps.creator.websiteOption') },
              { value: 'mobile-app', name: t('apps.creator.mobileAppOption') },
            ]}
            value={selectedOption}
            onChange={value => onChangeSelectedOption(value as 'website' | 'mobile-app')}
          />
        </Stack.Item>
        <Stack.Item fill>
          <Card connected>
            <Card.Section>
              <Text as="p" weight={600} dataId="Label">
                {t('apps.creator.siteDetailsLabel')}
              </Text>
            </Card.Section>
            <Card.Section subdued>
              <Stack vertical>
                <Stack.Item fill>
                  <TextFieldControl
                    label={t('apps.create.field.name')}
                    name="name"
                    autoFocus
                    required
                  />
                </Stack.Item>
                <Stack.Item fill>
                  <TagsInputControl
                    label={t('apps.create.field.urls')}
                    info={t('apps.create.help.urls')}
                    name="urls"
                    required
                    splitSeparators={SPLIT_SEPARATORS}
                  />
                </Stack.Item>
                <Stack.Item fill>
                  <FilteredSelectControl
                    label={t('apps.details.settings.general.timezone.label')}
                    info={timezoneOption ? t('apps.details.settings.general.timezone.help', {
                      date: formatDate(new Date(), {
                        format: 'LT',
                        timeZone: timezoneOption.intlValue ?? timezoneOption.id,
                      }),
                      currentTimezone: previewTimezone || 'UTC',
                    }) : null}
                    name="timezone"
                    search={timezoneSearch}
                    onSearch={e => onTimezoneSearch(e.target.value)}
                    onOpen={onTimezoneOpen}
                    getSelectedName={value => filteredTimezones.find(item => item.id === value)?.name || ''}
                    options={[{
                      id: 'timezone-select-field',
                      items: filteredTimezones,
                    }]}
                  />
                </Stack.Item>
                <Stack.Item fill>
                  <FilteredSelectControl
                    label={t('apps.details.settings.general.currency.label')}
                    info={t('apps.details.settings.general.currency-with-conversion.info')}
                    name="currency"
                    search={currencySearch}
                    onSearch={e => onCurrencySearch(e.target.value)}
                    onOpen={onCurrencyOpen}
                    getSelectedName={value => filteredCurrencies.find(item => item.id === value)?.name || ''}
                    options={[{
                      id: 'currency-select-field',
                      items: filteredCurrencies,
                    }]}
                  />
                </Stack.Item>
              </Stack>
            </Card.Section>
          </Card>
          <Card>
            <Card.Section>
              <Stack vertical spacing="wide">
                <Stack.Item>
                  <Text as="p" weight={600}>{t('apps.creator.reporting.title')}</Text>
                </Stack.Item>
                <Stack.Item previousSpacing="narrow">
                  <Text as="p" size={12} dataId="Caption" color="InkLighter">{t('apps.creator.reporting.description')}</Text>
                </Stack.Item>
              </Stack>
            </Card.Section>
            <Card.Section subdued>
              <Stack vertical>
                <Stack.Item>
                  <Stack wrap verticalAlignment="center">
                    <Stack.Item>
                      <SwitcherControl
                        name="realTimeDashboards"
                      />
                    </Stack.Item>
                    <Stack.Item fill>
                      <FormLabel htmlFor="realTimeDashboards" size="normal" noMargin>
                        {t('apps.creator.realTimeDashboards.switcherLabel')}
                      </FormLabel>
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
                <Stack.Item>
                  <Stack wrap verticalAlignment="center">
                    <Stack.Item>
                      <SwitcherControl
                        name="eCommerceTracking"
                      />
                    </Stack.Item>
                    <Stack.Item fill>
                      <FormLabel htmlFor="eCommerceTracking" size="normal" noMargin>
                        {t('apps.creator.eCommerceTracking.switcherLabel')}
                      </FormLabel>
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
              </Stack>
            </Card.Section>
          </Card>
        </Stack.Item>
      </Stack>
    </Layout>
  );
};

export default BasicConfigurationStep;
